import {useEffect, useRef, useState} from 'react'

const PROGRESS_CEILING = 95

export interface TimedProgressResult {
  progress: number
  ready: boolean
}

/**
 * Ease-out-cubic progress bar driven by elapsed time against a target duration,
 * capped at 95% until `done` flips true. Used both for the purely synthetic
 * icon/qr-style dialogs (random 7-10s duration) and the video-to-gif dialog's
 * hybrid real-estimate progress (duration = expected conversion time) — either
 * way this gives the upsell ad pane a minimum viewing window even when the
 * underlying work finishes faster than the duration.
 */
export const useTimedProgress = (done: boolean, durationMs: number | (() => number)): TimedProgressResult => {
  const [progress, setProgress] = useState(0)
  const [ready, setReady] = useState(false)
  const startRef = useRef<number | null>(null)
  const durationRef = useRef<number | null>(null)

  useEffect(() => {
    if (ready) return
    if (startRef.current === null) startRef.current = performance.now()
    // Resolved lazily here (not during render) so a random-picking duration
    // function is only invoked once, from an effect — calling Math.random
    // during render is impure and can produce unstable results across renders.
    if (durationRef.current === null) {
      durationRef.current = typeof durationMs === 'function' ? durationMs() : durationMs
    }

    let rafId = 0
    const tick = () => {
      const start = startRef.current ?? performance.now()
      const duration = durationRef.current ?? 1
      const elapsed = performance.now() - start
      const ratio = Math.min(1, elapsed / duration)
      const eased = 1 - Math.pow(1 - ratio, 3)
      const ceiling = done ? 100 : PROGRESS_CEILING
      setProgress(Math.min(eased * 100, ceiling))

      if (elapsed >= duration && done) {
        setProgress(100)
        setReady(true)
        return
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [ready, done, durationMs])

  return {progress, ready}
}
